import { WEBSITE_URL } from 'core-lib/data/common';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import fetchModelDetails from 'src/modules/newCars/services/fetchModelDetails';
import fetchPriceBreakdown from 'src/modules/newCars/services/fetchPriceBreakdown';
import fetchVariantDetails from 'src/modules/newCars/services/fetchVariantDetails';
import getCityList from 'src/modules/newCars/services/getCityList';
import {
  CityType,
  ModelDetailType,
  VariantsListType,
} from 'src/modules/newCars/types';
import { toPascalCase } from 'core-lib/utils/utils';
import { Button, StarRating, Typography } from 'ui';
import ChevronRight from 'ui/components/newIcons/ChevronRight';
import { Color, TypeScales } from 'ui/primitives';
import { AllVariants } from '../../../popups/AllVariants/AllVariants';
import { SelectCity } from '../../../popups/SelectCity/SelectCity';
import { UserDetailPopup } from '../../../popups/UserDetailPopup/UserDetailPopup';
import s from './CityVariantSelector.module.scss';
// import { Jumbo } from '../../../content/Jumbo/Jumbo';

export const CityVariantSelector = ({
  content,
  strapiContent,
  selectedVariant,
  selectedCity,
  setSelectedVariant,
  setSelectedCity,
  setPriceData,
  setVariantDetailedSpecification
}: {
  content: ModelDetailType;
  strapiContent: any;
  selectedCity: CityType;
  selectedVariant: VariantsListType;
  setSelectedVariant: (arg: VariantsListType) => void;
  setSelectedCity: (arg: CityType) => void;
  setPriceData?: any;
  setVariantDetailedSpecification?: any;
}) => {
  const [showVariantPopup, setShowVariantPopup] = useState(false);
  const [showCityPopup, setShowCityPopup] = useState(false);
  const [cityList, setCityList] = useState<CityType[]>([]);
  const router = useRouter();
  const [showUserPopup, setShowUserPopup] = useState(false);
  async function fetchCityList() {
    const { response, error }: any = await getCityList();
    if (response) {
      setCityList(response);
    }
  }
  useEffect(() => {
    if (showCityPopup) {
      fetchCityList();
    }
  }, [showCityPopup]);
  useEffect(() => {
    const queryCity = router?.query?.selectedCity;
    const queryCode = router?.query?.selectedCode;
    if (cityList?.length && queryCity && queryCode) {
      setSelectedCity({ name: queryCity as string, id: queryCode as string });
    }
  }, [router?.query, cityList]);

  async function fetchBreakdown(city, variant) {
    router.replace(
      `${WEBSITE_URL}/new-cars/${content?.modelSeoData?.brandSlug}-car/${content?.modelSeoData?.modelSlug
      }/price-in-${city?.name?.toLowerCase().replace(' ', '-')}`,
      undefined,
      { shallow: true }
    );
    document.cookie = `cityId=${city?.id}`;
    document.cookie = `variantId=${variant?.variantKey}`;
    document.cookie = `cityName=${city?.name}`;
    const { response: priceResponse } = await fetchPriceBreakdown({
      locationSlug: city?.id ?? city?.code,
      variantId: variant?.variantKey,
    });
    if (priceResponse && setPriceData) {
      setPriceData(priceResponse);
    }
  }

  async function getVariantSpecifications(city) {
    document.cookie = `cityId=${city?.id}`;
    const modelDetail = await fetchModelDetails(
      content?.modelSeoData?.modelSlug,
      city?.id
    );

    const contentVariant = await fetchVariantDetails({
      modelDetail: modelDetail,
      slug: `${selectedVariant?.slug}`,
    });
    if (contentVariant && setVariantDetailedSpecification) {
      setVariantDetailedSpecification(contentVariant?.detailedSpecifications);
    }
  }

  return (
    <div className={s.root}>
      <figure className={s.fig}>
        <img
          src={content?.hero?.image}
          alt={`${content?.modelSeoData?.brandSlug}-${content?.modelSeoData?.modelSlug}-image`}
        />
      </figure>
      <Typography
        label={`${content?.hero?.brandName} ${content?.hero?.modelName}`}
        variant={TypeScales.h1}
        className={s.heading}
      />
      <div className={s.variant_select}>
        <Typography label="Variant" variant={TypeScales.h3} />
        <button
          type="button"
          className={s.variant_btn}
          onClick={() => {
            setShowVariantPopup(true);
          }}
        >
          <Typography label={selectedVariant?.variantName} />
          <ChevronRight primaryColor={Color.grey_80} />
        </button>
      </div>
      <div className={s.city_select}>
        <Typography label="City" variant={TypeScales.h3} />
        <button
          type="button"
          className={s.city_btn}
          onClick={() => {
            setShowCityPopup(true);
          }}
        >
          <Typography label={toPascalCase(selectedCity?.name)} />
          <ChevronRight primaryColor={Color.grey_80} />
        </button>
      </div>
      <div className={s.btn}>
        <Button
          label="Book a test drive"
          fullwidth
          onClick={() => setShowUserPopup(true)}
        />
      </div>
      {showVariantPopup && (
        <AllVariants
          show={showVariantPopup}
          onClose={() => setShowVariantPopup(false)}
          onSelect={(variant) => {
            setSelectedVariant(variant);
            fetchBreakdown(selectedCity, variant);
            getVariantSpecifications(selectedCity);
          }}
          variantsList={content?.variants}
          modelName={content?.hero?.modelName}
          modelImage={content?.hero?.image}
          setSelectedVariant={setSelectedVariant}
          modelSlug={content?.modelSeoData?.modelSlug}
          brandSlug={content?.modelSeoData?.brandSlug}
        />
      )}
      {showCityPopup && (
        <SelectCity
          show={showCityPopup}
          onClose={() => setShowCityPopup(false)}
          onSelect={(city) => {
            setSelectedCity(city);
            fetchBreakdown(city, selectedVariant);
            setShowCityPopup(false);
            getVariantSpecifications(selectedCity);
          }}
          selectedCity={selectedCity}
          cityList={cityList}
        />
      )}
      {showUserPopup && (
        <UserDetailPopup
          show={showUserPopup}
          onClose={() => setShowUserPopup(false)}
          brandName={content?.hero?.brandName}
          modelName={content?.hero?.modelName}
        />
      )}
    </div>
  );
};
